/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Nov 26 2024
 **/

import { CSSProperties, useState } from "react";

import { EventHandler, useEventHandlers } from "@rfice/eventhandler";

import useWhiteboardStore from "../stores/useWhiteboardStore";
import { MODE, OBJECT_TYPE, PEN_TYPE } from "@drawing/index";
import {
	Button,
	ExpandButton,
	ButtonContainer,
	GridContent,
	SliderContent,
	ColorContent,
	Splitter
} from "../designStuff";
import Presets from "../presets";

type UndoRedoState = {
	undo: boolean;
	redo: boolean;
};

export default function DrawingToolbar({ whiteboardMode }: { whiteboardMode: WhiteboardMode }) {
	const whiteboard = useWhiteboardStore((state) => state.whiteboard);
	const toolState = useWhiteboardStore((state) => state.toolState);
	const toolbarKey = useWhiteboardStore((state) => state.toolbarKey);

	const [undoRedoAvailable, setUndoRedoAvailable] = useState<UndoRedoState>({ undo: false, redo: false });

	const updateMode = (e: HtmlMouseEvent) => {
		switch (e.currentTarget.id) {
			case "btn-cursor": {
				whiteboard?.updateMode(MODE.CURSOR);
				break;
			}
			case "btn-pointer": {
				whiteboard?.updateMode(MODE.POINTING);
				break;
			}
			case "btn-freeline": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.FREE_LINE });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-line": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.LINE });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-arrow": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.ARROW });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-marker": {
				whiteboard?.updateObjectStyle({ penType: PEN_TYPE.MARKER });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-circle": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.CIRCLE });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-rect": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.RECT });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-eraser": {
				whiteboard?.updateObjectStyle({ penType: PEN_TYPE.ERASER });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
			case "btn-emoji": {
				whiteboard?.updateObjectStyle({ objectType: OBJECT_TYPE.EMOJI });
				whiteboard?.updateMode(MODE.DRAWING);
				break;
			}
		}
	};

	const updateColor = (index: number) => {
		whiteboard?.updateObjectStyle({ strokeColor: Presets.penColors[index] });
	};

	const updateThickness = (value: number) => {
		whiteboard?.updateObjectStyle({ thickness: value });
	};

	const onImageClick = () => {
		EventHandler.fire("$DrawingToolbar::ImageClicked$");
	};

	const updateHistory = (e: HtmlMouseEvent) => {
		const state: HistoryState = e.currentTarget.name;
		whiteboard?.updateHistoryState[state]();
		EventHandler.fire("$DrawingToolbar::HistoryChange$", state);
	};

	// styles
	const containerStyle: CSSProperties = {
		flexDirection: whiteboardMode === "drawing" ? "row" : "column"
	};

	const buttonSize: CSSProperties = {
		width: whiteboardMode === "drawing" ? "clamp(20px, 4vw, 35px)" : "28px",
		height: whiteboardMode === "drawing" ? "clamp(20px, 4vw, 35px)" : "28px"
	};

	// receive events
	useEventHandlers(
		{
			onDrawingManagerUndoRedoStateUpdated: (undoRedoState: UndoRedoState) => {
				setUndoRedoAvailable(undoRedoState);
			}
		},
		[whiteboard]
	);

	return (
		<ButtonContainer key={toolbarKey} style={containerStyle}>
			<Button style={buttonSize} name="cursor" id="btn-cursor" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="pointer" id="btn-pointer" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="freeline" id="btn-freeline" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="line" id="btn-line" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="arrow" id="btn-arrow" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="marker" id="btn-marker" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="circle" id="btn-circle" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="rect" id="btn-rect" className={toolState} onClick={updateMode} />
			<Button style={buttonSize} name="eraser" id="btn-eraser" className={toolState} onClick={updateMode} />
			<ExpandButton whiteboardMode={whiteboardMode} name="color" id="btn-color">
				<GridContent contents={Presets.penColors} onClick={updateColor} Component={ColorContent} />
			</ExpandButton>
			<ExpandButton whiteboardMode={whiteboardMode} name="thickness" id="btn-thickness">
				<SliderContent thicknessRange={Presets.thicknessRange} onChange={updateThickness} />
			</ExpandButton>
			<Button style={buttonSize} name="image" id="btn-image" className={toolState} onClick={onImageClick} />
			{whiteboardMode === "drawing" && <Splitter />}
			<Button
				style={{ ...buttonSize, opacity: undoRedoAvailable.undo ? 1 : 0.2 }}
				name="undo"
				id="btn-undo"
				onClick={updateHistory}
			/>
			<Button
				style={{ ...buttonSize, opacity: undoRedoAvailable.redo ? 1 : 0.2 }}
				name="redo"
				id="btn-redo"
				onClick={updateHistory}
			/>
			<Button style={buttonSize} name="clearAll" id="btn-clearAll" onClick={updateHistory} />
		</ButtonContainer>
	);
}
